import type { FindingWithLineInfo } from 'ts/commons/code/RenderedCode.findings';
import type { LineBasedRegion } from 'typedefs/LineBasedRegion';

/** Layout information for a single finding marker. */
export type FindingWithLayoutInfo = FindingWithLineInfo & {
	/** The column index (0-based) where this finding marker should be rendered. */
	column: number;
};

/**
 * Calculates the layout for findings markers, assigning each finding to a column such that:
 *
 * 1. Longer (multi-line) findings get lower column numbers (leftmost)
 * 2. No two findings occupy the same column on the same line
 * 3. Each finding maintains its column consistently across all lines it spans
 *
 * @param findings All findings to layout
 * @returns Array of findings with assigned column indices (0-based)
 */
export function calculateFindingsLayout(findings: FindingWithLineInfo[]): FindingWithLayoutInfo[] {
	const getFreeColumnForRegion = buildColumnAssigner();
	return findings.sort(compareByLength).map(findingWithLineInfo => {
		const column = getFreeColumnForRegion(findingWithLineInfo.region);
		return { ...findingWithLineInfo, column };
	});
}

/**
 * Compares two findings for sorting purposes with the following priority:
 *
 * 1. Longer findings first (by line span)
 * 2. Earlier start line first (for findings with same length)
 * 3. Lexicographically by ID (for stable sorting)
 */
function compareByLength(a: FindingWithLineInfo, b: FindingWithLineInfo) {
	const spanA = span(a);
	const spanB = span(b);
	if (spanA !== spanB) {
		return spanB - spanA; // Longer findings first
	}
	if (a.region.start !== b.region.start) {
		return a.region.start - b.region.end; // Earlier start line first
	}
	return a.finding.id.localeCompare(b.finding.id); // Stable by ID
}

/** Calculates the number of lines a finding spans (inclusive). */
function span(finding: FindingWithLineInfo): number {
	return finding.region.end - finding.region.start + 1;
}

/**
 * Creates a column assigner function that uses a greedy algorithm to assign columns. The returned function finds the
 * lowest column number that is free across all lines in a given region, then marks that column as occupied for those
 * lines.
 */
function buildColumnAssigner() {
	const occupiedColumns = new Map<number, Set<number>>();
	return (region: LineBasedRegion) => {
		// Find the lowest column number that's free on ALL lines this finding spans
		let column = 0;
		for (; ; column++) {
			let isFree = true;
			for (let line = region.start; line <= region.end; line++) {
				if (occupiedColumns.get(line)?.has(column)) {
					isFree = false;
					break;
				}
			}
			if (isFree) {
				break;
			}
		}

		// Mark this column as occupied on all lines the finding spans
		for (let line = region.start; line <= region.end; line++) {
			if (!occupiedColumns.has(line)) {
				occupiedColumns.set(line, new Set());
			}
			occupiedColumns.get(line)!.add(column);
		}
		return column;
	};
}
